import { motion } from 'framer-motion';

interface StackedBarChartItem {
  name: string;
  count: number;
  color: string;
}

interface StackedBarChartProps {
  items: Array<StackedBarChartItem>;
  total: number;
  height?: string;
  showLegend?: boolean;
  showTotal?: boolean;
  totalLabel?: string;
  animationDelay?: number;
  onItemClick?: (item: StackedBarChartItem) => void;
}

export function StackedBarChart({
  items,
  total,
  height = 'h-8',
  showLegend = true,
  showTotal = true,
  totalLabel,
  animationDelay = 0.3,
  onItemClick,
}: StackedBarChartProps) {
  if (items.length === 0 || total === 0) {
    return (
      <p className="text-sm text-slate-400 py-8 text-center">
        Aucune donnée disponible
      </p>
    );
  }

  return (
    <>
      {/* Stacked bar */}
      <div className={`${height} w-full overflow-hidden rounded-full flex`}>
        {items.map((item, i) => (
          <motion.div
            key={item.name}
            className="h-full"
            style={{ backgroundColor: item.color }}
            initial={{ width: 0 }}
            animate={{
              width: `${(item.count / total) * 100}%`,
            }}
            transition={{
              duration: 1,
              ease: 'easeOut',
              delay: animationDelay + i * 0.12,
            }}
          />
        ))}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="mt-6 flex flex-col gap-1">
          {items.map((item) => {
            const row = (
              <>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-slate-600 truncate">
                    {item.name}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-sm font-bold text-slate-900">
                    {item.count}
                  </span>
                  <span className="text-xs text-slate-400 w-8 text-right">
                    {total > 0
                      ? Math.round((item.count / total) * 100)
                      : 0}
                    %
                  </span>
                </div>
              </>
            );

            if (onItemClick) {
              return (
                <button
                  type="button"
                  key={item.name}
                  onClick={() => onItemClick(item)}
                  className="cursor-pointer flex items-center justify-between rounded-lg px-2 py-2 -mx-2 text-left transition-colors hover:bg-slate-50"
                >
                  {row}
                </button>
              );
            }

            return (
              <div
                key={item.name}
                className="flex items-center justify-between px-2 py-2 -mx-2"
              >
                {row}
              </div>
            );
          })}
        </div>
      )}

      {/* Total */}
      {showTotal && (
        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
          <span className="text-sm font-medium text-slate-500">
            Total
          </span>
          <span className="text-sm font-bold text-slate-900">
            {totalLabel || `${total}`}
          </span>
        </div>
      )}
    </>
  );
}
